/**
 * Is the BPMN modeler on `window`, and — when it is not — why.
 *
 * Both halves live here because every caller needs them together: a probe that
 * says "absent" is useless without the cause, and the cause is only meaningful
 * against a fresh probe. The presence half used to be duplicated verbatim in the
 * DSL executor and in `workflow-tools`' navigate skip-guard; two copies of a
 * readiness predicate is exactly how one of them drifts.
 *
 * The cause half is recorded at the failure site and read by the DSL executor's
 * abort path (KOA-18136).
 *
 * `WorkFlowWindow.componentDidMount` builds the modeler inside an ASYNC lifecycle
 * method, so anything `createModeler` throws becomes an unhandled rejection that
 * React never surfaces: `this.modeler` stays undefined, the "Loading…" overlay
 * never clears, and the only self-heal (`reconcileModeler`) needs a re-render that
 * never comes. From the outside this is indistinguishable from a mount still in
 * flight, so `ui_workflow_dsl` told the model "still mounting — re-send unchanged"
 * and the turn burned three identical 15s retries against a permanently dead
 * canvas with the real error visible NOWHERE (a browser console opened after the
 * fact does not retain it).
 *
 * This module is the channel that carries the real cause out: the failure site
 * stamps it, `describeModelerAbsence` reads it, and it lands in the tool response
 * — the surface that IS captured in the mcp-chat-server trace.
 */

/**
 * True when the BPMN modeler is actually mounted and usable — i.e.
 * `window.bpmnModeler` exists and exposes `get`.
 *
 * A set `selectedWf` is NOT proof the modeler is built: `create_workflow` can set
 * `selectedWf` while WorkFlowWindow's mount-time build races or misses on a cold
 * first run, leaving `window.bpmnModeler` undefined. Anything that decides
 * "the canvas is ready" must ask THIS, not the selection. (KOA-17362)
 */
export function isBpmnModelerReady(): boolean {
  const modeler = (window as unknown as { bpmnModeler?: { get?: unknown } }).bpmnModeler;
  return !!modeler && typeof modeler.get === "function";
}

/**
 * Generous ceiling for the modeler-mount wait. A chat-driven `create_workflow`
 * (openAfterCreate) returns the instant `setSelectedWf` resolves and does NOT
 * await the async WorkFlowWindow mount → `createModeler` → `window.bpmnModeler`
 * build, which on a COLD first-run measurably takes ~10s (KOA-17362 live trace).
 * The old 4s ceiling expired mid-mount, so the first graph script ran against a
 * missing modeler and half-failed. The wait returns the instant the modeler is
 * ready (no fixed delay when it is already present, and param-only scripts skip
 * the wait entirely), so a larger ceiling only ever costs time in the genuinely-
 * not-yet-mounted case — exactly when waiting is correct. 15s comfortably covers
 * the observed cold-mount without waiting forever on a truly stuck mount.
 */
export const MODELER_READY_TIMEOUT_MS: number = 15000;

/**
 * Wait until the live BPMN modeler is mounted, or the timeout elapses.
 *
 * A chat-driven `create_workflow` (openAfterCreate) switches the active view to
 * the process-automation canvas AND sets `selectedWf`, but WorkFlowWindow's mount
 * → `prepareModeller` → `window.bpmnModeler = …` is ASYNCHRONOUS (React render +
 * `await createModeler`). Anything dispatched immediately after therefore races
 * that build: `window.bpmnModeler` is still undefined and every graph command
 * fails "BPMN Modeler is not ready or not found." (KOA-17362 — modeler-mount
 * race: the committed `componentDidMount` build-if-selected fix only guarantees
 * the modeler is EVENTUALLY built, not that it is ready the instant the caller
 * runs.) Polling here bridges that gap deterministically. Read-only callers are
 * unaffected — when the modeler is already present this returns on the first
 * check with no delay. Resolves `true` once ready, `false` after the timeout.
 */
export async function waitForBpmnModelerReady(
  timeoutMs: number = MODELER_READY_TIMEOUT_MS,
  intervalMs: number = 100
): Promise<boolean> {
  if (isBpmnModelerReady()) return true;
  const deadline: number = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise<void>((resolve) => setTimeout(resolve, intervalMs));
    if (isBpmnModelerReady()) return true;
  }
  return isBpmnModelerReady();
}

/** A recorded modeler-build failure. */
export interface IModelerBuildFailure {
  /** Human-readable cause, already trimmed for a tool message. */
  readonly reason: string;
  /** Workflow the build was attempted for, when known. */
  readonly workflowId?: string;
}

interface IDiagnosticsWindow {
  __wfModelerBuildFailure?: IModelerBuildFailure;
}

function diagnosticsWindow(): IDiagnosticsWindow | undefined {
  if (typeof window === "undefined") return undefined;
  return window as unknown as IDiagnosticsWindow;
}

/** Normalize anything `catch` can hand us into a single readable line. */
function describeThrown(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ? `${error.message} @ ${error.stack.split("\n")[1]?.trim() ?? "?"}` : error.message;
  }
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/** Record why a modeler build failed. Never throws — diagnostics must not add failures. */
export function recordModelerBuildFailure(error: unknown, workflowId?: string): void {
  try {
    const target: IDiagnosticsWindow | undefined = diagnosticsWindow();
    if (!target) return;
    target.__wfModelerBuildFailure = { reason: describeThrown(error), workflowId };
    // Also to the console, for the case where DevTools IS open at the time.
    console.error("[workflow] BPMN modeler build failed:", error);
  } catch {
    /* diagnostics only */
  }
}

/** Forget the last failure — call once a build succeeds, so stale causes never mislead. */
export function clearModelerBuildFailure(): void {
  try {
    const target: IDiagnosticsWindow | undefined = diagnosticsWindow();
    if (target) target.__wfModelerBuildFailure = undefined;
  } catch {
    /* diagnostics only */
  }
}

/** The last recorded failure, if any. */
export function readModelerBuildFailure(): IModelerBuildFailure | undefined {
  try {
    return diagnosticsWindow()?.__wfModelerBuildFailure;
  } catch {
    return undefined;
  }
}

/**
 * KOA-18136 §37 — WHERE THE CANVAS WENT, ON A CHANNEL THE AUTHOR ACTUALLY READS.
 *
 * The build failures this exists for are invisible at the only place that could
 * report them: a reconcile that refuses to subtract, or a command loop that stops
 * because the canvas is no longer the one it started on, both happen deep inside the
 * browser. A `console.error` there is not a channel — nobody opens DevTools after the
 * fact, and the buffer does not survive. So the incidents are recorded here and
 * rendered onto the TOOL RESPONSE, which is the surface the orchestrator trace keeps.
 *
 * Bounded on purpose: a long script must not be able to turn diagnostics into a leak,
 * and the author needs the FIRST incidents (where it started going wrong) far more
 * than the hundredth.
 */

/** A canvas read or continuity failure, as recorded at the site that saw it. */
export interface IWorkflowCanvasIncidentInput {
  /** What was being attempted — "reconcile", "lookup:Activity_1", "command:#42 CONNECT". */
  readonly purpose: string;
  /** Machine-ish cause: "modeler-absent" | "empty-read" | "replaced" | "lost". */
  readonly cause: string;
  /** Extra evidence for the message (missing ids, root ids, …). */
  readonly detail?: string;
  /** Was `window.bpmnModeler` present? A service/window divergence is its own bug. */
  readonly windowModelerPresent: boolean;
  /** Elements the read returned. */
  readonly elementCount: number;
}

/** A recorded incident, in the order it was seen. */
export interface IWorkflowCanvasIncident extends IWorkflowCanvasIncidentInput {
  readonly seq: number;
}

/** How many incidents are kept. The first ones carry the cause. */
const CANVAS_INCIDENT_LIMIT: number = 20;

interface ICanvasDiagnosticsWindow {
  __wfCanvasIncidents?: IWorkflowCanvasIncident[];
  __wfCanvasIncidentCount?: number;
  __wfLastGoodCanvasElementCount?: number;
}

function canvasDiagnosticsWindow(): ICanvasDiagnosticsWindow | undefined {
  if (typeof window === "undefined") return undefined;
  return window as unknown as ICanvasDiagnosticsWindow;
}

/** Record one canvas incident. Never throws — diagnostics must not add failures. */
export function recordCanvasIncident(incident: IWorkflowCanvasIncidentInput): void {
  try {
    const target: ICanvasDiagnosticsWindow | undefined = canvasDiagnosticsWindow();
    if (!target) return;
    const seen: number = (target.__wfCanvasIncidentCount ?? 0) + 1;
    target.__wfCanvasIncidentCount = seen;
    const kept: IWorkflowCanvasIncident[] = target.__wfCanvasIncidents ?? [];
    if (kept.length < CANVAS_INCIDENT_LIMIT) kept.push({ ...incident, seq: seen });
    target.__wfCanvasIncidents = kept;
  } catch {
    /* diagnostics only */
  }
}

/** Remember the last read that WAS trustworthy — the "it was fine until here" number. */
export function noteCanvasReadable(elementCount: number): void {
  try {
    const target: ICanvasDiagnosticsWindow | undefined = canvasDiagnosticsWindow();
    if (target) target.__wfLastGoodCanvasElementCount = elementCount;
  } catch {
    /* diagnostics only */
  }
}

/** Elements the last trustworthy read returned, if there was one. */
export function readLastGoodCanvasElementCount(): number | undefined {
  try {
    return canvasDiagnosticsWindow()?.__wfLastGoodCanvasElementCount;
  } catch {
    return undefined;
  }
}

/** Incidents recorded so far (kept ones), oldest first. */
export function readCanvasIncidents(): readonly IWorkflowCanvasIncident[] {
  try {
    return canvasDiagnosticsWindow()?.__wfCanvasIncidents ?? [];
  } catch {
    return [];
  }
}

/** How many incidents were seen in total, including the ones the limit dropped. */
export function readCanvasIncidentCount(): number {
  try {
    return canvasDiagnosticsWindow()?.__wfCanvasIncidentCount ?? 0;
  } catch {
    return 0;
  }
}

/** Start a fresh window — called at the top of a script so causes never leak across runs. */
export function clearCanvasIncidents(): void {
  try {
    const target: ICanvasDiagnosticsWindow | undefined = canvasDiagnosticsWindow();
    if (!target) return;
    target.__wfCanvasIncidents = [];
    target.__wfCanvasIncidentCount = 0;
  } catch {
    /* diagnostics only */
  }
}

/**
 * The incidents, rendered for a tool response. Empty string when there were none —
 * the common case, and one that must add nothing to the answer.
 */
export function describeCanvasIncidents(): string {
  const incidents: readonly IWorkflowCanvasIncident[] = readCanvasIncidents();
  if (incidents.length === 0) return "";
  const total: number = readCanvasIncidentCount();
  const lines: string = incidents
    .map((incident: IWorkflowCanvasIncident): string => {
      const detail: string = incident.detail ? ` — ${incident.detail}` : "";
      const windowNote: string = incident.windowModelerPresent ? "" : ", window.bpmnModeler absent";
      return `   • #${incident.seq} ${incident.purpose}: ${incident.cause} (elements=${incident.elementCount}${windowNote})${detail}`;
    })
    .join("\n");
  const dropped: string = total > incidents.length ? `\n   • …${total - incidents.length} more not listed` : "";
  const lastGood: number | undefined = readLastGoodCanvasElementCount();
  const lastGoodNote: string =
    lastGood === undefined ? "" : `\n   Last trustworthy canvas read returned ${lastGood} elements.`;
  return `\n\nCANVAS DIAGNOSTICS (${total} incident(s)):\n${lines}${dropped}${lastGoodNote}`;
}
